import React, { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  BookmarkCheck,
  ChevronLeft,
  ChevronRight,
  FileText,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { ExternalTender } from '@/types';
import { apiService } from '@/services/apiService';
import { addMonths, buildMonthCells, sameDay, startOfMonth } from '@/lib/dateUtils';
import { Button, IconButton } from '@/components/ui/Button';

type CalendarStatus = 'ACTIVE' | 'PARTICIPATION' | 'ARCHIVED' | 'DISCARDED';

type TenderCalendarEntry = {
  tender: ExternalTender;
  date: Date | null;
  status: CalendarStatus;
};

const STATUS_META: Record<CalendarStatus, {
  label: string;
  className: string;
  dotClassName: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}> = {
  ACTIVE: {
    label: 'Oportunidades ativas',
    className: 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-300',
    dotClassName: 'bg-sky-500',
    Icon: FileText,
  },
  PARTICIPATION: {
    label: 'Em participação',
    className: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300',
    dotClassName: 'bg-emerald-500',
    Icon: BookmarkCheck,
  },
  ARCHIVED: {
    label: 'Participações arquivadas',
    className: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
    dotClassName: 'bg-amber-500',
    Icon: Archive,
  },
  DISCARDED: {
    label: 'Descartadas',
    className: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300',
    dotClassName: 'bg-rose-500',
    Icon: Trash2,
  },
};

const STATUS_ORDER: CalendarStatus[] = ['ACTIVE', 'PARTICIPATION', 'ARCHIVED', 'DISCARDED'];
const DOW_LABELS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

function parseOpportunityDate(value?: string | null): Date | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
  }
  const br = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (br) {
    const [, day, month, year, hour = '12', minute = '00', second = '00'] = br;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const portal = raw.match(/^(\d{4})-(\d{2})-(\d{2}):(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (portal) {
    const [, year, month, day, hour, minute, second = '00'] = portal;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function calendarStatus(tender: ExternalTender): CalendarStatus {
  if (tender.ativo === false) return 'DISCARDED';
  if (tender.favoriteTenderId) {
    return tender.favoriteArchiveReason ? 'ARCHIVED' : 'PARTICIPATION';
  }
  return 'ACTIVE';
}

function formatDate(value: Date | null): string {
  if (!value) return 'Sem prazo informado';
  return value.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatReferenceValue(value?: number | null): string {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return 'Não informado';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericValue);
}

function normalizeSearch(value: unknown): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

const TenderOpportunityCalendar: React.FC = () => {
  const [entries, setEntries] = useState<TenderCalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [enabledStatuses, setEnabledStatuses] = useState<Record<CalendarStatus, boolean>>({
    ACTIVE: true,
    PARTICIPATION: true,
    ARCHIVED: true,
    DISCARDED: true,
  });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const tenders = await apiService.getExternalTenders('ALL');
      setEntries(tenders.map((tender) => ({
        tender,
        date: parseOpportunityDate(tender.encerramento || tender.recebimentoPropostas || tender.incluido),
        status: calendarStatus(tender),
      })));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Não foi possível carregar o calendário de oportunidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const term = normalizeSearch(search).trim();
    return entries.filter((entry) => {
      if (!enabledStatuses[entry.status]) return false;
      if (!term) return true;
      return [entry.tender.edital, entry.tender.orgao, entry.tender.objeto, entry.tender.modalidade, entry.tender.estado, entry.tender.cidade]
        .some((value) => normalizeSearch(value).includes(term));
    });
  }, [enabledStatuses, entries, search]);

  const byDay = useMemo(() => filtered.reduce<Record<string, TenderCalendarEntry[]>>((groups, entry) => {
    if (!entry.date) return groups;
    const key = entry.date.toDateString();
    (groups[key] ||= []).push(entry);
    return groups;
  }, {}), [filtered]);

  const reportEntries = useMemo(() => {
    const selectedKey = selectedDay?.toDateString();
    return filtered
      .filter((entry) => !selectedKey || entry.date?.toDateString() === selectedKey)
      .sort((left, right) => (left.date?.getTime() || Number.MAX_SAFE_INTEGER) - (right.date?.getTime() || Number.MAX_SAFE_INTEGER));
  }, [filtered, selectedDay]);

  const counts = useMemo(() => STATUS_ORDER.reduce<Record<CalendarStatus, number>>((result, status) => {
    result[status] = entries.filter((entry) => entry.status === status).length;
    return result;
  }, { ACTIVE: 0, PARTICIPATION: 0, ARCHIVED: 0, DISCARDED: 0 }), [entries]);

  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);
  const currentMonthLabel = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="max-w-[1480px] mx-auto px-4 sm:px-6 py-5 space-y-5">
      <section className="flex flex-wrap items-start justify-between gap-4 border-b border-line dark:border-line-dark pb-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-subtitle dark:text-ink-subtitle-dark">Licitações</div>
          <h1 className="mt-1 text-xl font-semibold text-ink-title dark:text-ink-title-dark">Calendário de oportunidades</h1>
          <p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Relatório por prazo de propostas, incluindo participação, arquivos e descartes.</p>
        </div>
        <Button variant="secondary" onClick={() => void load()} disabled={loading} icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}>
          Atualizar
        </Button>
      </section>

      <section className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
        <label className="relative block min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-placeholder dark:text-ink-placeholder-dark" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por edital, órgão, objeto, modalidade ou UF"
            className="input h-10 w-full pl-9 text-sm"
          />
        </label>
        <div className="flex items-center justify-between gap-1 rounded-md border border-line dark:border-line-dark px-1 py-1">
          <IconButton aria-label="Mês anterior" title="Mês anterior" onClick={() => setCursor((value) => addMonths(value, -1))}><ChevronLeft size={15} /></IconButton>
          <button type="button" onClick={() => setCursor(startOfMonth(new Date()))} className="min-w-36 px-2 text-sm font-medium capitalize text-ink-title dark:text-ink-title-dark">{currentMonthLabel}</button>
          <IconButton aria-label="Próximo mês" title="Próximo mês" onClick={() => setCursor((value) => addMonths(value, 1))}><ChevronRight size={15} /></IconButton>
        </div>
      </section>

      <section className="flex flex-wrap gap-2" aria-label="Filtrar situação das oportunidades">
        {STATUS_ORDER.map((status) => {
          const meta = STATUS_META[status];
          const Icon = meta.Icon;
          const active = enabledStatuses[status];
          return (
            <button
              key={status}
              type="button"
              onClick={() => setEnabledStatuses((current) => ({ ...current, [status]: !current[status] }))}
              className={`inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-opacity ${meta.className} ${active ? '' : 'opacity-40'}`}
              aria-pressed={active}
            >
              <Icon size={15} />
              {meta.label}
              <span className="rounded bg-white/70 px-1.5 py-0.5 text-[11px] dark:bg-black/20">{counts[status]}</span>
            </button>
          );
        })}
      </section>

      {error && (
        <section className="flex items-center justify-between gap-3 border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">
          <span>{error}</span>
          <Button variant="secondary" onClick={() => void load()}>Tentar novamente</Button>
        </section>
      )}

      <section className="border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
        <div className="grid grid-cols-7 border-b border-line dark:border-line-dark">
          {DOW_LABELS.map((label) => <div key={label} className="px-2 py-2 text-[10px] font-semibold tracking-[0.08em] text-ink-subtitle dark:text-ink-subtitle-dark">{label}</div>)}
        </div>
        {loading ? (
          <div className="grid grid-cols-7 [grid-auto-rows:minmax(108px,1fr)] animate-pulse">
            {Array.from({ length: 42 }).map((_, index) => <div key={index} className="border-b border-r border-line dark:border-line-dark bg-surface-alt/50 dark:bg-surface-dark-alt/50" />)}
          </div>
        ) : (
          <div className="grid grid-cols-7 [grid-auto-rows:minmax(108px,1fr)]">
            {cells.map((cell, index) => {
              const entriesForDay = cell.out ? [] : (byDay[cell.date.toDateString()] || []);
              const visible = entriesForDay.slice(0, 3);
              const isToday = !cell.out && sameDay(cell.date, new Date());
              const selected = !cell.out && sameDay(cell.date, selectedDay || new Date(0));
              return (
                <button
                  key={cell.date.toISOString()}
                  type="button"
                  disabled={cell.out}
                  onClick={() => !cell.out && setSelectedDay(cell.date)}
                  className={[
                    'min-w-0 border-b border-line p-2 text-left transition-colors dark:border-line-dark',
                    (index + 1) % 7 === 0 ? '' : 'border-r',
                    cell.out ? 'bg-surface-alt/40 text-ink-placeholder dark:bg-surface-dark-alt/40 dark:text-ink-placeholder-dark' : 'hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                    isToday ? 'ring-1 ring-inset ring-brand-500' : '',
                    selected ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.12)]' : '',
                  ].join(' ')}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className={`text-xs font-semibold tabular-nums ${isToday ? 'text-brand-700 dark:text-brand-300' : ''}`}>{String(cell.date.getDate()).padStart(2, '0')}</span>
                    {entriesForDay.length > 0 && <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark">{entriesForDay.length}</span>}
                  </div>
                  <div className="space-y-1">
                    {visible.map((entry) => {
                      const meta = STATUS_META[entry.status];
                      return <div key={entry.tender.id} className="flex min-w-0 items-center gap-1.5 text-[10.5px] text-ink-text dark:text-ink-text-dark"><span className={`h-1.5 w-1.5 flex-none rounded-full ${meta.dotClassName}`} /><span className="truncate">{entry.tender.edital || entry.tender.orgao}</span></div>;
                    })}
                    {entriesForDay.length > visible.length && <div className="pl-3 text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark">+{entriesForDay.length - visible.length} mais</div>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 dark:border-line-dark">
          <div>
            <h2 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">{selectedDay ? `Relatório de ${selectedDay.toLocaleDateString('pt-BR')}` : 'Relatório de oportunidades'}</h2>
            <p className="mt-0.5 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{reportEntries.length} registro(s) conforme os filtros aplicados.</p>
          </div>
          {selectedDay && <Button variant="secondary" onClick={() => setSelectedDay(null)}>Ver todas</Button>}
        </div>
        {loading ? null : reportEntries.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma oportunidade corresponde aos filtros deste relatório.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left text-sm">
              <thead className="bg-surface-alt text-[10.5px] uppercase tracking-[0.06em] text-ink-subtitle dark:bg-surface-dark-alt dark:text-ink-subtitle-dark">
                <tr><th className="px-4 py-3 font-semibold">Prazo</th><th className="px-4 py-3 font-semibold">Situação</th><th className="px-4 py-3 font-semibold">Edital</th><th className="px-4 py-3 font-semibold">Órgão</th><th className="px-4 py-3 font-semibold">Objeto</th><th className="px-4 py-3 text-right font-semibold">Valor referencial</th><th className="px-4 py-3 font-semibold">UF</th></tr>
              </thead>
              <tbody className="divide-y divide-line dark:divide-line-dark">
                {reportEntries.map((entry) => {
                  const meta = STATUS_META[entry.status];
                  return (
                    <tr key={entry.tender.id} className="align-top hover:bg-surface-alt/60 dark:hover:bg-surface-dark-alt/60">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{formatDate(entry.date)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs font-medium ${meta.className}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dotClassName}`} />{meta.label}</span></td>
                      <td className="px-4 py-3 font-medium text-ink-title dark:text-ink-title-dark">{entry.tender.edital || 'Não informado'}</td>
                      <td className="px-4 py-3 text-ink-text dark:text-ink-text-dark">{entry.tender.orgao || 'Não informado'}</td>
                      <td className="max-w-[460px] px-4 py-3 leading-5 text-ink-text dark:text-ink-text-dark">{entry.tender.objeto || 'Objeto não informado'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-ink-title dark:text-ink-title-dark">{formatReferenceValue(entry.tender.valorTotal)}</td>
                      <td className="px-4 py-3 text-ink-subtitle dark:text-ink-subtitle-dark">{entry.tender.estado || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default TenderOpportunityCalendar;
